import React, { useState } from "react";

const InvoiceField = ({
    label,
    type = "text",
    value,
    onChange,
    placeholder,
    options = [],
    required = false,
    minLength,
    maxLength,
    min,
    max,
    rows="1",
    disabled=false,
    searchable = false,
}) => {
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");


    const patterns = {
        string: /^[A-Za-z ]*$/,
        number: /^[0-9]*$/,
        alphanumeric: /^[A-Za-z0-9]*$/,
        pancard: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
        gst: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
    };

    const validate = (val) => {
        let err = "";

        if (required && !val) {
            err = `${label} is required`;
        } else if (type === "string" && val && !patterns.string.test(val)) {
            err = "Only alphabets allowed";
        } else if (type === "number" && val && !patterns.number.test(val)) {
            err = "Only digits allowed";
        } else if (type === "alphanumeric" && val && !patterns.alphanumeric.test(val)) {
            err = "Only alphabets & numbers allowed";
        } else if (type === "pancard" && val && !patterns.pancard.test(val.toUpperCase())) {
            err = "Invalid PAN format (ABCDE1234F)";
        } else if (type === "gst" && val && !patterns.gst.test(val.toUpperCase())) {
            err = "Invalid GST format";
        }

        // Length validation
        if (!err && minLength && val.length < minLength) {
            err = `${label} must be at least ${minLength} characters`;
        }
        if (!err && maxLength && val.length > maxLength) {
            alert(`${label} cannot exceed ${maxLength} characters`);
            return;
        }

        setError(err);
        return !err;
    };

    const handleChange = (e) => {
        let val = e.target.value;

        if (type === "pancard" || type === "gst") {
            val = val.toUpperCase();
        }

         if (maxLength && val.length > maxLength) {
        setError(`${label} cannot exceed ${maxLength} characters`);
        return;
    }


        if (validate(val)) {
            onChange(val);
        } else {
            onChange(val);
        }
    };

    // Dropdown
    if (type === "dropdown") {
        return (
            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
                <select
                    value={value || ""}
                    onChange={handleChange}
                    disabled={disabled}
                    className={`w-full px-3 py-2 border ${error ? "border-red-500" : "border-gray-300"
                        } rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                    <option value="">Select</option>
                    {options.map((opt, idx) => (
                        <option key={idx} value={opt.value || opt}>
                            {opt.label || opt}
                        </option>
                    ))}
                </select>
                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </div>
        );
    }

    // Textarea
    if (type === "description") {
        return (
            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
                <textarea
                    value={value || ""}
                    onChange={handleChange}
                    placeholder={placeholder}
                    rows={rows}
                    className={`w-full px-3 py-2 border ${error ? "border-red-500" : "border-gray-300"
                        } rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
                />
                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </div>
        );
    }

    // Date Picker
    if (type === "date") {
        return (
            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
                <input
                    type="date"
                    value={value || ""}
                    onChange={handleChange}
                    min={min}
                    max={max}
                    className={`w-full px-3 py-2 border ${error ? "border-red-500" : "border-gray-300"
                        } rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </div>
        );
    }

    // Dropdown (Always open downward + Optional search)
    if (type === "dropdown") {

        const normalizedOptions = options.map(opt => ({
            label: typeof opt === "string" ? opt.replace(/_/g, " ") : opt.label,
            value: typeof opt === "string" ? opt : opt.value,
        }));

        const filteredOptions = searchable
            ? normalizedOptions.filter(opt =>
                opt.label.toLowerCase().includes(search.toLowerCase())
            )
            : normalizedOptions;

        return (
            <div className="w-full relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>

                {/* Search input (optional) */}
                {searchable && (
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search..."
                        className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                )}

                <select
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className={`w-full px-3 py-2 border ${error ? "border-red-500" : "border-gray-300"
                        } rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                    <option value="">Select</option>

                    {filteredOptions.map((opt, idx) => (
                        <option key={idx} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>

                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </div>
        );
    }


    // Normal Input
    return (
        <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <input
                type={["string", "alphanumeric", "pancard", "gst"].includes(type) ? "text" : type}
                value={value || ""}
                onChange={handleChange}
                maxLength={maxLength}
                placeholder={placeholder}
                className={`w-full px-3 py-2 border ${error ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
    );
};

export default InvoiceField;
